import { useEffect, useRef, useCallback } from "react";
import { ICE_CONFIG } from "@/lib/iceConfig";
import { logDebug, logWarn } from "@/lib/log";

const CANVAS_W = 1280;
const CANVAS_H = 720;
const IDLE_FPS = 30;
const IDLE_FRAME_MS = 33;
const PING_INTERVAL_MS = 20000;
const RECONNECT_DELAY_MS = 3000;
const TRACK_SYNC_INTERVAL_MS = 2000;
const MAX_BITRATE_BPS = 6000000;

function drawIdleFrame(ctx, t) {
    const cx = CANVAS_W / 2;
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.strokeStyle = "rgba(255,255,255,0.25)";
    ctx.lineWidth = 2;
    const c = 28;
    const m = 16;
    [
        [m, m, 1, 1],
        [CANVAS_W - m, m, -1, 1],
        [m, CANVAS_H - m, 1, -1],
        [CANVAS_W - m, CANVAS_H - m, -1, -1],
    ].forEach(([x, y, dx, dy]) => {
        ctx.beginPath();
        ctx.moveTo(x + dx * c, y);
        ctx.lineTo(x, y);
        ctx.lineTo(x, y + dy * c);
        ctx.stroke();
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.font = "bold 72px 'Oswald', 'Arial Black', sans-serif";
    ctx.fillText("AWAITING SIGNAL", cx, 350);
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "18px 'JetBrains Mono', monospace";
    ctx.fillText("S P C L I V E", cx, 410);
    // subtle pulse keeps captureStream emitting 30fps so the encoder ramps to full res
    const pulse = 0.15 + 0.2 * (0.5 + 0.5 * Math.sin(t / 500));
    ctx.fillStyle = `rgba(255,69,0,${pulse.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(cx, 455, 4, 0, Math.PI * 2);
    ctx.fill();
}

function startIdleCanvas() {
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext("2d");
    drawIdleFrame(ctx, 0);
    const timer = setInterval(() => drawIdleFrame(ctx, performance.now()), IDLE_FRAME_MS);
    const track = canvas.captureStream(IDLE_FPS).getVideoTracks()[0];
    return {
        track,
        stop: () => {
            clearInterval(timer);
            track.stop();
        },
    };
}

const wsSend = (ctx, data) => {
    if (ctx.wsRef.current?.readyState === 1) ctx.wsRef.current.send(JSON.stringify(data));
};

const liveTrack = (t) => (t && t.readyState === "live" ? t : null);

function syncSenderTracks(pcs, videoSenders, audioSenders, videoTrack, micTrack) {
    Object.entries(pcs).forEach(([vid, pc]) => {
        if (pc.connectionState === "closed") return;
        const vSender = videoSenders[vid];
        if (videoTrack && vSender && vSender.track !== videoTrack) {
            vSender.replaceTrack(videoTrack).catch(() => {});
        }
        const aSender = audioSenders[vid];
        if (aSender && aSender.track !== micTrack) {
            aSender.replaceTrack(micTrack).catch(() => {});
        }
    });
}

function closePeer(ctx, viewerId) {
    const pc = ctx.pcs[viewerId];
    if (pc) {
        try {
            pc.close();
        } catch (e) {
            logWarn("[SPCLIVE MIRROR] pc close failed", e);
        }
    }
    delete ctx.pcs[viewerId];
    delete ctx.offerIds[viewerId];
    delete ctx.pendingIce[viewerId];
    delete ctx.videoSenders[viewerId];
    delete ctx.audioSenders[viewerId];
}

function closeAllPeers(ctx) {
    Object.keys(ctx.pcs).forEach((vid) => closePeer(ctx, vid));
}

async function tuneSender(sender) {
    try {
        const params = sender.getParameters();
        params.degradationPreference = "maintain-framerate";
        params.encodings = [{ maxBitrate: MAX_BITRATE_BPS }];
        await sender.setParameters(params);
    } catch (e) {
        logWarn("[SPCLIVE MIRROR] setParameters failed", e);
    }
}

async function createOffer(ctx, viewerId) {
    closePeer(ctx, viewerId);
    const pc = new RTCPeerConnection(ICE_CONFIG);
    const offerId = Math.random().toString(36).slice(2, 10);
    ctx.pcs[viewerId] = pc;
    ctx.offerIds[viewerId] = offerId;
    ctx.pendingIce[viewerId] = [];
    const track = ctx.getCurrentTrack();
    const shared = track ? new MediaStream([track]) : new MediaStream();
    const sender = track
        ? pc.addTrack(track, shared)
        : pc.addTransceiver("video", { direction: "sendonly", streams: [shared] }).sender;
    await tuneSender(sender);
    ctx.videoSenders[viewerId] = sender;
    const audioSender = pc.addTransceiver("audio", { direction: "sendonly", streams: [shared] }).sender;
    ctx.audioSenders[viewerId] = audioSender;
    const mic = ctx.getMicTrack();
    if (mic) audioSender.replaceTrack(mic).catch(() => {});
    pc.onicecandidate = (e) => {
        if (e.candidate) {
            wsSend(ctx, { type: "ice", viewer_id: viewerId, candidate: e.candidate, offer_id: offerId });
        }
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    wsSend(ctx, { type: "offer", viewer_id: viewerId, sdp: offer.sdp, offer_id: offerId });
}

async function applyAnswer(ctx, msg, pc) {
    if (msg.offer_id !== ctx.offerIds[msg.viewer_id]) return;
    if (pc.signalingState !== "have-local-offer") return;
    await pc.setRemoteDescription({ type: "answer", sdp: msg.sdp });
    (ctx.pendingIce[msg.viewer_id] || []).forEach((c) => pc.addIceCandidate(c).catch(() => {}));
    ctx.pendingIce[msg.viewer_id] = [];
}

async function applyViewerIce(ctx, msg, pc) {
    if (msg.offer_id && msg.offer_id !== ctx.offerIds[msg.viewer_id]) return;
    if (pc.remoteDescription) {
        await pc.addIceCandidate(msg.candidate).catch(() => {});
    } else {
        ctx.pendingIce[msg.viewer_id]?.push(msg.candidate);
    }
}

async function handlePublisherMessage(ctx, msg) {
    const pc = ctx.pcs[msg.viewer_id];
    if (msg.type === "viewer-joined") {
        await createOffer(ctx, msg.viewer_id);
    } else if (msg.type === "answer" && pc) {
        await applyAnswer(ctx, msg, pc);
    } else if (msg.type === "ice" && msg.candidate && pc) {
        await applyViewerIce(ctx, msg, pc);
    } else if (msg.type === "viewer-left") {
        closePeer(ctx, msg.viewer_id);
    }
}

function openPublisherSocket(ctx, obsToken, control) {
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws/mirror/${obsToken}?role=publisher`);
    ctx.wsRef.current = ws;
    let pingTimer = null;

    ws.onopen = () => {
        if (control.isDisposed() || ctx.wsRef.current !== ws) return;
        logDebug("[SPCLIVE MIRROR] publisher connected");
        pingTimer = setInterval(() => {
            if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" }));
        }, PING_INTERVAL_MS);
    };
    ws.onmessage = async (ev) => {
        if (control.isDisposed() || ctx.wsRef.current !== ws) return;
        try {
            await handlePublisherMessage(ctx, JSON.parse(ev.data));
        } catch (e) {
            logWarn("[SPCLIVE MIRROR] signaling error", e);
        }
    };
    ws.onclose = (e) => {
        if (pingTimer) clearInterval(pingTimer);
        if (control.isDisposed() || ctx.wsRef.current !== ws) return;
        closeAllPeers(ctx);
        if (e.code === 4409) {
            logWarn("[SPCLIVE MIRROR] another Studio tab took over publishing — stopped");
            return;
        }
        control.scheduleReconnect();
    };
}

export function useMirrorPublisher({ obsToken, remoteVideoRef, isLive, micTrackRef, micOn }) {
    const wsRef = useRef(null);
    const pcsRef = useRef({});
    const offerIdsRef = useRef({});
    const pendingIceRef = useRef({});
    const videoSendersRef = useRef({});
    const audioSendersRef = useRef({});
    const canvasTrackRef = useRef(null);
    const isLiveRef = useRef(isLive);
    isLiveRef.current = isLive;

    const getCurrentTrack = useCallback(() => {
        if (isLiveRef.current) {
            const ms = remoteVideoRef?.current?.srcObject;
            const track = ms?.getVideoTracks?.()[0];
            if (track && track.readyState === "live") return track;
        }
        return canvasTrackRef.current;
    }, [remoteVideoRef]);

    const getMicTrack = useCallback(() => liveTrack(micTrackRef?.current), [micTrackRef]);

    const updateOutgoingTracks = useCallback(() => {
        syncSenderTracks(
            pcsRef.current,
            videoSendersRef.current,
            audioSendersRef.current,
            getCurrentTrack(),
            getMicTrack()
        );
    }, [getCurrentTrack, getMicTrack]);

    useEffect(() => {
        updateOutgoingTracks();
    }, [isLive, micOn, updateOutgoingTracks]);

    useEffect(() => {
        if (!obsToken) return undefined;
        let disposed = false;
        let reconnectTimer = null;
        const idle = startIdleCanvas();
        canvasTrackRef.current = idle.track;
        const ctx = {
            wsRef,
            pcs: pcsRef.current,
            offerIds: offerIdsRef.current,
            pendingIce: pendingIceRef.current,
            videoSenders: videoSendersRef.current,
            audioSenders: audioSendersRef.current,
            getCurrentTrack,
            getMicTrack,
        };
        const control = {
            isDisposed: () => disposed,
            scheduleReconnect: () => {
                reconnectTimer = setTimeout(() => {
                    if (!disposed) openPublisherSocket(ctx, obsToken, control);
                }, RECONNECT_DELAY_MS);
            },
        };
        openPublisherSocket(ctx, obsToken, control);

        const trackTimer = setInterval(updateOutgoingTracks, TRACK_SYNC_INTERVAL_MS);

        return () => {
            disposed = true;
            clearInterval(trackTimer);
            if (reconnectTimer) clearTimeout(reconnectTimer);
            closeAllPeers(ctx);
            try {
                wsRef.current?.close();
            } catch (e) {
                logWarn("[SPCLIVE MIRROR] ws close failed", e);
            }
            wsRef.current = null;
            idle.stop();
            canvasTrackRef.current = null;
        };
    }, [obsToken, getCurrentTrack, getMicTrack, updateOutgoingTracks]);
}
